import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { CouponUserApi } from 'api';
import type {
  CouponUser,
  CouponUserBulk,
  CouponUserDeleteBulk,
} from 'api/methods/CouponUserApi';

/**
 * [CouponUser] Query key 생성
 * --
 */
export const couponUserQueryKeys = createQueryKeys('couponuser', {
  getCouponUser: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getCouponUsers: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [CouponUser] 회원 쿠폰 전체 조회
 * --
 */
export const useGetCouponUsers = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: couponUserQueryKeys.getCouponUsers(params).queryKey,
    queryFn: () => CouponUserApi.getCouponUsers(params),
    ...options,
  });
  return data;
};

/**
 * [CouponUser] 회원 쿠폰 상세 조회
 * --
 */
export const useGetCouponUser = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: couponUserQueryKeys.getCouponUser(idx, params).queryKey,
    queryFn: () => CouponUserApi.getCouponUser(idx, params),
  });

  return data;
};

/**
 * [CouponUser] 회원 쿠폰 생성
 * --
 */
export const useCreateCouponUser = () => {
  const { mutateAsync: createCouponUser, ...result } = useMutation({
    mutationFn: (variables: CouponUser) =>
      CouponUserApi.createCouponUser(variables),
  });

  return { createCouponUser, ...result };
};

/**
 * [CouponUser] 회원 쿠폰 수정
 * --
 */
export const useUpdateCouponUser = () => {
  const { mutateAsync: updateCouponUser, ...result } = useMutation({
    mutationFn: (variables: CouponUser) =>
      CouponUserApi.updateCouponUser(variables),
  });

  return { updateCouponUser, ...result };
};

/**
 * [CouponUser] 회원 쿠폰 삭제
 * --
 */
export const useDeleteCouponUser = () => {
  const { mutateAsync: deleteCouponUser, ...result } = useMutation({
    mutationFn: (variables: number) =>
      CouponUserApi.deleteCouponUser(variables),
  });

  return { deleteCouponUser, ...result };
};

/**
 * [CouponUser] 회원 쿠폰 발급
 * --
 */
export const useRegisterCouponUser = () => {
  const { mutateAsync: registerCouponUser, ...result } = useMutation({
    mutationFn: (variables: CouponUser) =>
      CouponUserApi.registerCouponUser(variables),
  });

  return { registerCouponUser, ...result };
};

/**
 * [CouponUser] 회원 쿠폰 벌크 생성
 * --
 */
export const useBulkCreateCouponUser = () => {
  const { mutateAsync: bulkCreateCouponUser, ...result } = useMutation({
    mutationFn: (variables: CouponUserBulk) =>
      CouponUserApi.bulkCreateCouponUser(variables),
  });

  return { bulkCreateCouponUser, ...result };
};
/**
 * [CouponUser] 회원 쿠폰 벌크 수정
 * --
 */
export const useBulkUpdateCouponUser = () => {
  const { mutateAsync: bulkUpdateCouponUser, ...result } = useMutation({
    mutationFn: (variables: CouponUserBulk[]) =>
      CouponUserApi.bulkUpdateCouponUser(variables),
  });

  return { bulkUpdateCouponUser, ...result };
};

/**
 * [CouponUser] 회원 쿠폰 벌크 삭제
 * --
 */
export const useBulkDeleteCouponUser = () => {
  const { mutateAsync: bulkDeleteCouponUser, ...result } = useMutation({
    mutationFn: (variables: CouponUserDeleteBulk[]) =>
      CouponUserApi.bulkDeleteCouponUser(variables),
  });

  return { bulkDeleteCouponUser, ...result };
};
